import {
  Chart as ChartJS,
  Filler, Legend, LineElement, PointElement, RadialLinearScale, Tooltip
} from 'chart.js';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowRight, BookOpen,
  CheckCircle2, ChevronDown, ChevronLeft,
  ChevronRight,
  ChevronUp,
  FileText, GitBranch,
  HelpCircle,
  History,
  Lightbulb, Maximize2,
  Network, PanelLeftClose, PanelLeftOpen, School,
  Shield, Sparkles,
  TrendingUp,
  View,
  Volume2, VolumeX, X, Zap
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import SignalVisualizer from '../../components/SignalVisualizer';
import api from '../../config/axiosConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { usePesa } from '../../stores/usePesaEngine';
import { K2000VoiceScanner } from './K2000VoiceScanner';
import RapportEnquete from './RapportEnquete';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ─── Design Tokens (alignés landing PESACORE) ─────────────────────────────────

const T = {
  bg:        '#050a0e',
  bgPanel:   '#080d12',
  bgCard:    '#0b1219',
  border:    '#1a2a38',
  accent:    '#e07b20',
  accentG:   '#f59e0b',
  cyan:      '#0ea5e9',
  cyanG:     '#38bdf8',
  green:     '#10b981',
  purple:    '#8b5cf6',
  red:       '#f87171',
  gold:      '#C9A55A',
  goldGlow:  'rgba(201,165,90,0.35)',
  text:      '#f7f7f7',
  textDim:   'rgba(250, 250, 250, 0.93)',
  textMute:  'rgba(255, 255, 255, 0.45)',
  scan:      'rgba(14,165,233,0.018)',
  // surfaces PESA sim
  surface0:  'rgba(5,10,14,0.97)',
  surface1:  'rgba(8,13,18,0.93)',
  surface2:  'rgba(11,18,25,0.85)',
  border0:   'rgba(26,42,56,0.7)',
  border1:   'rgba(26,42,56,1)',
};

const MONO    = 'text-[9px] font-mono tracking-[0.22em] uppercase';
const MONO_SM = 'text-[8px] font-mono tracking-[0.18em] uppercase';

const FAMILY_COLORS = {
  École: '#2357aadc', Travail: '#ef4444', Justice: '#6366f1',
  Art: '#f59e0b', Médiation: '#10b981', Famille: '#8b5cf6', Santé: '#ec4899'
};

const AMBIANCE_PALETTE = {
  tension:    { bg: 'rgba(239,68,68,0.35)',   particle: '#ef4444', glow: '#7f1d1d', accent: '#fca5a5' },
  hope:       { bg: 'rgba(16,185,129,0.28)',  particle: '#10b981', glow: '#064e3b', accent: '#6ee7b7' },
  crisis:     { bg: 'rgba(245,159,11,0.25)',  particle: '#f59e0b', glow: '#78350f', accent: '#fcd34d' },
  resolution: { bg: 'rgba(99,101,241,0.30)',  particle: '#818cf8', glow: '#1e1b4b', accent: '#c7d2fe' },
  default:    { bg: 'rgba(14,165,233,0.12)',  particle: '#0ea5e9', glow: '#0c1a26', accent: '#7dd3fc' },
};

const ARCHETYPE_ORBIT = {
  opprimé:      { speed: 0.8,  radius: 1.0, pulse: true,  color: '#ef4444' },
  reproducteur: { speed: 0.3,  radius: 1.2, pulse: false, color: '#6366f1' },
  médiateur:    { speed: 0.6,  radius: 1.1, pulse: true,  color: '#10b981' },
  subversif:    { speed: 1.4,  radius: 0.9, pulse: true,  color: '#f59e0b' },
  institution:  { speed: 0.15, radius: 1.4, pulse: false, color: '#94a3b8' },
};

const TENSION_CONFIG = {
  1: { chaos: 0,    nodeJitter: 0,    linkOpacity: 0.04 },
  2: { chaos: 0.02, nodeJitter: 0.5,  linkOpacity: 0.06 },
  3: { chaos: 0.05, nodeJitter: 1.2,  linkOpacity: 0.08 },
  4: { chaos: 0.10, nodeJitter: 2.5,  linkOpacity: 0.12 },
  5: { chaos: 0.18, nodeJitter: 4.0,  linkOpacity: 0.18 },
};

// ─── Primitives UI (landing-aligned) ─────────────────────────────────────────

/** Grille causale subtile calquée sur landing */
const LabGrid = () => (
  <div
    className="fixed inset-0 pointer-events-none z-0"
    style={{
      backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`,
      backgroundSize: '52px 52px',
      opacity: 0.28,
    }}
  />
);

/** Scanlines rémanentes */
const Scanlines = () => (
  <div
    className="fixed inset-0 pointer-events-none z-[1]"
    style={{
      backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`,
      opacity: 1,
    }}
  />
);

const StatusTag = ({ children, color = 'purple' }) => {
  const map = {
    purple:  'bg-violet-500/10 border-violet-500/20 text-violet-400',
    amber:   'bg-amber-500/10  border-amber-500/20  text-amber-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    red:     'bg-red-500/10    border-red-500/20    text-red-400',
    slate:   'bg-white/5       border-white/10      text-white/40',
    cyan:    'bg-cyan-500/10   border-cyan-500/20   text-cyan-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[9px] font-mono tracking-widest ${map[color] || map.slate}`}>
      {children}
    </span>
  );
};

const Pulse = ({ color = T.cyan, size = 6 }) => (
  <span className="relative flex" style={{ width: size, height: size }}>
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
      style={{ background: color }} />
    <span className="relative inline-flex rounded-full h-full w-full"
      style={{ background: color }} />
  </span>
);

// ─── [1] ProbabilityBridge ────────────────────────────────────────────────────

export const ProbabilityBridge = () => {
  const { engine, phase, currentSolution } = usePesa();
  const [showExplainer, setShowExplainer] = useState(false);

  const theoretical = useMemo(() => {
    const raw = engine?.state?.optimalPath?.probabilityOfSuccess
      ?? engine?.state?.prediction?.probability ?? 0;
    return Math.max(0, Math.min(100, raw > 1 ? raw : raw * 100));
  }, [engine?.state]);

  const real = useMemo(() => {
    if (phase === 'finished' && currentSolution?.interpretation) {
      const r = currentSolution.interpretation.realWorldProbability
        ?? currentSolution.interpretation.theoreticalPromise ?? 10;
      return Math.max(0, Math.min(100, r > 1 ? r : r * 100));
    }
    return Math.round(theoretical * 0.58);
  }, [phase, currentSolution, theoretical]);

  const gap      = theoretical - real;
  const gapColor = gap > 30 ? T.red : gap > 15 ? T.accentG : T.green;

  const historicalNote = useMemo(() => {
    if (gap > 40) return 'Bourdieu (1970) : 88% des tentatives d\'émancipation scolaire ont été réabsorbées par la reproduction sociale.';
    if (gap > 25) return 'Foucault (1975) : les dispositifs institutionnels neutralisent 70% des transformations initiées par la base.';
    if (gap > 10) return 'Crozier & Friedberg (1977) : les acteurs stratégiques ajustent leurs résistances à l\'ampleur du changement.';
    return 'Weber (1922) : quand la légitimité est construite, la transformation peut s\'institutionnaliser durablement.';
  }, [gap]);

  if (theoretical === 0) return null;

  return (
    <>
      <button
        onClick={() => setShowExplainer(true)}
        className="flex flex-col gap-1.5 group cursor-pointer w-full text-left"
        title="Comprendre l'écart théorique / réel"
      >
        <div className="flex items-center justify-between">
          <span className={`${MONO_SM} text-white/25`}>Δ Théorique / Réel</span>
          <div className="flex items-center gap-1.5">
            <span className={`${MONO_SM} font-bold tabular-nums`} style={{ color: gapColor }}>
              −{gap.toFixed(0)}%
            </span>
            <HelpCircle size={9} className="text-white/20 group-hover:text-white/50 transition-colors" />
          </div>
        </div>
        <div className="space-y-1">
          {[
            { label: 'Théorique', value: theoretical, color: T.purple },
            { label: 'Historique', value: real, color: T.gold },
          ].map(item => (
            <div key={item.label}>
              <div className="h-0.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${item.value}%` }}
                  transition={{ duration: 1.2, ease: 'easeOut' }}
                  className="h-full rounded-full"
                  style={{ background: item.color, boxShadow: `0 0 6px ${item.color}60` }}
                />
              </div>
              <div className="flex justify-between mt-0.5">
                <span className={`${MONO_SM}`} style={{ color: `${item.color}70` }}>{item.label}</span>
                <span className={`${MONO_SM} font-bold`} style={{ color: item.color }}>{item.value.toFixed(0)}%</span>
              </div>
            </div>
          ))}
        </div>
      </button>

      {createPortal(
        <AnimatePresence>
          {showExplainer && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
              style={{ background: 'rgba(0,0,0,0.90)', backdropFilter: 'blur(14px)' }}
              onClick={() => setShowExplainer(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 10 }}
                onClick={e => e.stopPropagation()}
                className="max-w-lg w-full rounded-2xl overflow-hidden"
                style={{ background: T.surface0, border: `1px solid ${T.border1}`, boxShadow: '0 0 80px rgba(0,0,0,0.9)' }}
              >
                <div className="px-6 py-5 border-b flex items-start justify-between" style={{ borderColor: T.border0 }}>
                  <div>
                    <h3 className="text-base font-semibold" style={{ color: T.text }}>L'écart théorique / réel</h3>
                    <p className={`${MONO_SM} mt-0.5`} style={{ color: `${T.purple}80` }}>Calibration bayésienne</p>
                  </div>
                  <button onClick={() => setShowExplainer(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white transition-colors">
                    <X size={15} />
                  </button>
                </div>
                <div className="p-6 space-y-5">
                  <div className="space-y-4">
                    {[
                      { label: 'Probabilité théorique', sub: 'Calculée par optimisation génétique dans la simulation', value: theoretical, color: T.purple },
                      { label: 'Probabilité historique réelle', sub: 'Fréquence observée sur des cas similaires (1900–2025)', value: real, color: T.gold },
                    ].map(item => (
                      <div key={item.label}>
                        <div className="flex justify-between mb-2">
                          <div>
                            <p className="text-[11px] font-semibold" style={{ color: T.textDim }}>{item.label}</p>
                            <p className="text-[10px] mt-0.5" style={{ color: T.textMute }}>{item.sub}</p>
                          </div>
                          <span className="text-2xl font-bold font-mono tabular-nums" style={{ color: item.color }}>{item.value.toFixed(0)}%</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: item.color }} />
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1" style={{ background: `${gapColor}40` }} />
                      <span className="text-[10px] font-mono font-bold" style={{ color: gapColor }}>ABSORPTION : −{gap.toFixed(0)}%</span>
                      <div className="h-px flex-1" style={{ background: `${gapColor}40` }} />
                    </div>
                  </div>
                  <div className="p-4 rounded-xl space-y-2" style={{ background: T.surface1, border: `1px solid ${T.border0}` }}>
                    <p className="text-[11px] leading-relaxed" style={{ color: T.textMute }}>
                      La simulation modélise les conditions optimales. Dans le monde réel, les forces de reproduction sociale créent un <span className="font-medium" style={{ color: T.textDim }}>écart structurel</span> entre ce qui est théoriquement possible et ce qui se réalise.
                    </p>
                    <p className="text-[10px] italic pt-2 border-t leading-relaxed" style={{ borderColor: T.border0, color: `${T.gold}90` }}>
                      "{historicalNote}"
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

// ─── [2] InsightOverlay ───────────────────────────────────────────────────────

export const useInsightOverlay = () => {
  const { engine, completedActions } = usePesa();
  const [insight, setInsight] = useState(null);
  const prevCountRef = useRef(0);
  const timerRef = useRef(null);
  const completedArr = completedActions instanceof Set ? [...completedActions] : [];

  useEffect(() => {
    if (completedArr.length <= prevCountRef.current) return;
    prevCountRef.current = completedArr.length;
    const lastId = completedArr[completedArr.length - 1];
    if (!lastId) return;
    const action = engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === lastId);
    if (!action) return;
    const params  = engine?.state?.parameters || {};
    const initial = engine?.state?.initialParameters || {};
    const deltas  = Object.entries(params)
      .map(([k, v]) => ({ key: k, before: initial[k] ?? 50, after: v, delta: v - (initial[k] ?? 50) }))
      .filter(d => Math.abs(d.delta) >= 2)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 4);
    setInsight({
      id: lastId,
      label: action.label,
      whatHappened:   action.deepFeedback?.whatHappened,
      whyItMatters:   action.deepFeedback?.whyItMatters,
      boostCondition: action.deepFeedback?.boostCondition,
      competency:     action.competencySignal,
      causalChain:    (action.causalChain || []).slice(0, 2),
      deltas,
    });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setInsight(null), 60000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [completedArr.length]);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setInsight(null);
  }, []);

  return { insight, dismiss };
};

export const InsightOverlay = () => {
  const { insight, dismiss } = useInsightOverlay();
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!insight) { setProgress(100); return; }
    setProgress(100);
    const start    = Date.now();
    const duration = 60000;
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 100 - (elapsed / duration) * 100));
      if (elapsed >= duration) clearInterval(id);
    }, 80);
    return () => clearInterval(id);
  }, [insight?.id]);

  return createPortal(
    <AnimatePresence>
      {insight && (
        <motion.div
          key={insight.id}
          initial={{ opacity: 0, x: 40, scale: 0.96 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 40, scale: 0.96 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-8 right-8 z-[500] w-80 rounded-2xl overflow-hidden"
          style={{
            background: T.surface0,
            border: `1px solid ${T.border1}`,
            boxShadow: `0 0 60px rgba(0,0,0,0.85), 0 0 0 1px ${T.border0}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Barre de progression */}
          <div className="h-0.5" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div
              className="h-full"
              style={{
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${T.purple}, ${T.gold})`,
                transition: 'width 0.08s linear',
              }}
            />
          </div>

          <div className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Zap size={10} style={{ color: T.accentG }} />
                  <span className={`${MONO_SM}`} style={{ color: `${T.accentG}80` }}>Action jouée</span>
                </div>
                <p className="text-[13px] font-semibold leading-tight" style={{ color: T.text }}>{insight.label}</p>
              </div>
              <button onClick={dismiss} className="p-1 rounded-lg hover:bg-white/8 text-white/25 hover:text-white/60 transition-colors shrink-0">
                <X size={13} />
              </button>
            </div>

            {insight.competency && (
              <div className="flex items-center gap-2">
                <Shield size={9} style={{ color: T.textMute }} className="shrink-0" />
                <span className="text-[10px]" style={{ color: T.textMute }}>{insight.competency.name}</span>
                <StatusTag color="amber">{insight.competency.level}</StatusTag>
              </div>
            )}

            {insight.whatHappened && (
              <p className="text-[11px] leading-relaxed" style={{ color: T.textMute }}>{insight.whatHappened}</p>
            )}

            {insight.deltas.length > 0 && (
              <div className="grid grid-cols-2 gap-1.5">
                {insight.deltas.map(d => (
                  <div
                    key={d.key}
                    className="flex items-center justify-between px-2.5 py-1.5 rounded-lg"
                    style={{
                      background: d.delta > 0 ? `${T.green}08` : `${T.red}08`,
                      border: `1px solid ${d.delta > 0 ? `${T.green}20` : `${T.red}20`}`,
                    }}
                  >
                    <span className={`${MONO_SM} truncate`} style={{ color: T.textMute }}>{d.key}</span>
                    <span
                      className={`${MONO_SM} font-bold tabular-nums ml-1`}
                      style={{ color: d.delta > 0 ? T.green : T.red }}
                    >
                      {d.delta > 0 ? '+' : ''}{d.delta}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {insight.causalChain.length > 0 && (
              <div className="space-y-1">
                {insight.causalChain.map((link, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <span className="truncate max-w-[100px]" style={{ color: T.textMute }}>{link.from}</span>
                    <ArrowRight size={8} className="shrink-0" style={{ color: 'rgba(255,255,255,0.15)' }} />
                    <span className="truncate" style={{ color: `${T.green}90` }}>{link.to}</span>
                  </div>
                ))}
              </div>
            )}

            {insight.boostCondition && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-lg"
                style={{ background: `${T.gold}08`, border: `1px solid ${T.goldGlow}` }}
              >
                <TrendingUp size={9} style={{ color: `${T.gold}90` }} className="shrink-0 mt-0.5" />
                <p className="text-[10px] italic leading-relaxed" style={{ color: `${T.gold}80` }}>{insight.boostCondition}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ─── [3] DebriefOnboarding ────────────────────────────────────────────────────

export const DebriefOnboarding = ({ onComplete, onClose }) => {
  const { engine, currentSolution, completedActions } = usePesa();
  const [step, setStep] = useState(0);
  const completedArr = completedActions instanceof Set ? [...completedActions] : [];

  const theoretical = useMemo(() => {
    const interpTheo = currentSolution?.interpretation?.theoreticalPromise;
    if (interpTheo != null) return Math.max(0, Math.min(100, interpTheo > 1 ? interpTheo : interpTheo * 100));
    const raw = engine?.state?.optimalPath?.probabilityOfSuccess ?? engine?.state?.prediction?.probability ?? 0;
    return Math.max(0, Math.min(100, raw > 1 ? raw : raw * 100));
  }, [engine?.state, currentSolution]);

  const real = useMemo(() => {
    if (currentSolution?.interpretation) {
      const r = currentSolution.interpretation.realWorldProbability ?? currentSolution.interpretation.theoreticalPromise ?? 10;
      return Math.max(0, Math.min(100, r > 1 ? r : r * 100));
    }
    return Math.round(theoretical * 0.58);
  }, [currentSolution, theoretical]);

  const gap = theoretical - real;

  const keyActions = useMemo(() => completedArr.slice(0, 5).map(id => {
    const action = engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === id);
    return action ? { id, label: action.label, competency: action.competencySignal } : null;
  }).filter(Boolean), [completedArr, engine?.case?.steps]);

  const missedCFs = useMemo(() =>
    (engine?.counterfactuals || []).sort((a, b) => b.deltaProbability - a.deltaProbability).slice(0, 3),
    [engine?.counterfactuals]
  );

  const slides = [
    {
      id: 'choices',
      icon: CheckCircle2,
      color: T.green,
      title: 'Ce que vous avez construit',
      subtitle: `${completedArr.length} décision${completedArr.length > 1 ? 's' : ''} prises`,
      content: (
        <div className="space-y-3">
          <p className="text-[13px] leading-relaxed" style={{ color: T.textMute }}>
            Voici les choix qui ont le plus façonné la trajectoire de cette simulation. Chacun a déclenché des effets en cascade que le rapport détaillera.
          </p>
          <div className="space-y-2">
            {keyActions.length > 0 ? keyActions.map((a, i) => (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{ background: T.surface2, border: `1px solid ${T.border0}` }}>
                <span className="text-[10px] font-mono font-bold tabular-nums w-5 shrink-0" style={{ color: T.textMute }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-[12px] flex-1 truncate" style={{ color: T.textDim }}>{a.label}</span>
                {a.competency && <StatusTag color="amber">{a.competency.level}</StatusTag>}
              </div>
            )) : (
              <p className={`text-center py-4 ${MONO} italic`} style={{ color: T.textMute }}>Aucune action enregistrée.</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'gap',
      icon: GitBranch,
      color: T.gold,
      title: 'Pourquoi les chiffres divergent',
      subtitle: `−${gap.toFixed(0)}% d'absorption systémique`,
      content: (
        <div className="space-y-4">
          <p className="text-[13px] leading-relaxed" style={{ color: T.textMute }}>
            La simulation vous a donné <strong style={{ color: T.text }}>{theoretical.toFixed(0)}%</strong> de probabilité théorique.
            Les données historiques réelles indiquent <strong style={{ color: T.text }}>{real.toFixed(0)}%</strong>.
            Ce n'est pas un bug — c'est la leçon centrale.
          </p>
          <div className="space-y-3 p-4 rounded-xl" style={{ background: T.surface2, border: `1px solid ${T.border0}` }}>
            {[
              { label: 'Probabilité théorique', value: theoretical, color: T.purple },
              { label: 'Probabilité historique réelle', value: real, color: T.gold },
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between mb-1.5">
                  <span className="text-[11px]" style={{ color: T.textMute }}>{item.label}</span>
                  <span className="text-[12px] font-mono font-bold tabular-nums" style={{ color: item.color }}>{item.value.toFixed(0)}%</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: item.color }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] italic leading-relaxed px-1" style={{ color: T.textMute }}>
            Les forces de reproduction sociale (Bourdieu), les résistances institutionnelles (Foucault) et les jeux d'acteurs (Crozier) absorbent systématiquement une partie du potentiel transformateur.
          </p>
        </div>
      ),
    },
    {
      id: 'unlocked',
      icon: Sparkles,
      color: T.purple,
      title: 'Ce que vous auriez pu débloquer',
      subtitle: missedCFs.length > 0
        ? `${missedCFs.length} levier${missedCFs.length > 1 ? 's' : ''} non activé${missedCFs.length > 1 ? 's' : ''}`
        : 'Trajectoire optimale atteinte',
      content: (
        <div className="space-y-4">
          <p className="text-[13px] leading-relaxed" style={{ color: T.textMute }}>
            Le rapport complet révèle les chemins alternatifs. Voici un aperçu des leviers qui auraient eu le plus d'impact.
          </p>
          {missedCFs.length > 0 ? (
            <div className="space-y-2.5">
              {missedCFs.map((cf, i) => (
                <div key={i} className="p-3 rounded-xl" style={{ background: T.surface2, border: `1px solid ${T.border0}` }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div
                      className="px-2 py-0.5 rounded text-[9px] font-mono font-bold"
                      style={{ background: `${T.purple}18`, color: T.purple, border: `1px solid ${T.purple}30` }}
                    >
                      +{(cf.deltaProbability * 100).toFixed(0)}%
                    </div>
                    <span className={`${MONO_SM}`} style={{ color: T.textMute }}>impact potentiel</span>
                  </div>
                  <p className="text-[11px]" style={{ color: T.textDim }}>{cf.actionTitle}</p>
                  {cf.description && (
                    <p className="text-[10px] mt-1 leading-relaxed italic line-clamp-2" style={{ color: T.textMute }}>{cf.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-6 gap-3">
              <CheckCircle2 size={28} style={{ color: T.green }} />
              <p className="text-[12px] text-center" style={{ color: `${T.green}80` }}>Vous avez activé les leviers les plus déterminants.</p>
            </div>
          )}
          <div
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
            style={{ background: `${T.purple}08`, border: `1px dashed ${T.purple}30` }}
          >
            <Lightbulb size={11} style={{ color: `${T.purple}80` }} className="shrink-0" />
            <p className="text-[10px] italic leading-relaxed" style={{ color: T.textMute }}>
              La simulation est rejouable. Des conditions initiales différentes mènent à des trajectoires différentes.
            </p>
          </div>
        </div>
      ),
    },
  ];

  const current = slides[step];
  const Icon    = current.icon;
  const isLast  = step === slides.length - 1;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9000] flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.94)', backdropFilter: 'blur(18px)' }}
    >
      {/* Grille subtile en fond du modal */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`,
          backgroundSize: '52px 52px',
          opacity: 0.15,
        }}
      />
      <motion.div
        key={current.id}
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.97 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="relative max-w-xl w-full rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: T.bgPanel,
          border: `1px solid ${T.border}`,
          boxShadow: `0 0 120px rgba(0,0,0,0.9), 0 0 0 1px ${current.color}18`,
          maxHeight: '90vh',
        }}
      >
        {/* Accent line top */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${current.color}60, transparent)` }}
        />

        {/* Scanlines */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`,
          }}
        />

        {/* Progress dots */}
        <div className="relative z-10 flex items-center justify-between px-6 pt-5 pb-0 shrink-0">
          <div className="flex gap-2">
            {slides.map((s, i) => (
              <div
                key={s.id}
                className="rounded-full transition-all duration-300"
                style={{
                  width:  i === step ? '20px' : '6px',
                  height: '6px',
                  background: i === step ? current.color : T.border,
                }}
              />
            ))}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-white/25 hover:text-white/60 transition-colors">
            <X size={14} />
          </button>
        </div>

        {/* Header */}
        <div className="relative z-10 px-6 py-5 shrink-0">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: `${current.color}14`, border: `1px solid ${current.color}28` }}
          >
            <Icon size={18} style={{ color: current.color }} />
          </div>
          <div className={`${MONO_SM} mb-2`} style={{ color: `${current.color}60` }}>
            {String(step + 1).padStart(2, '0')} / {slides.length}
          </div>
          <h2 className="text-xl font-semibold leading-tight" style={{ color: T.text }}>{current.title}</h2>
          <p className={`${MONO} mt-1.5`} style={{ color: `${current.color}70` }}>{current.subtitle}</p>
        </div>

        {/* Content */}
        <div
          className="relative z-10 flex-1 overflow-y-auto px-6 pb-4"
          style={{ scrollbarWidth: 'thin', scrollbarColor: `${T.border} transparent` }}
        >
          {current.content}
        </div>

        {/* Footer */}
        <div
          className="relative z-10 px-6 py-5 flex items-center justify-between shrink-0 border-t"
          style={{ borderColor: T.border0 }}
        >
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-mono transition-colors"
            style={{ color: T.textMute }}
          >
            <ChevronLeft size={13} />
            {step === 0 ? 'Annuler' : 'Précédent'}
          </button>
          <button
            onClick={() => isLast ? onComplete() : setStep(s => s + 1)}
            className="flex items-center gap-2.5 px-5 py-2.5 rounded-xl font-semibold text-[12px] transition-all"
            style={{
              background: isLast
                ? `linear-gradient(135deg, ${T.purple}, ${T.gold})`
                : `${current.color}18`,
              color:  isLast ? '#fff' : current.color,
              border: `1px solid ${current.color}35`,
              boxShadow: isLast ? `0 0 20px ${T.purple}40` : 'none',
            }}
          >
            {isLast ? (
              <><FileText size={13} />Ouvrir le rapport complet</>
            ) : (
              <>Suivant<ChevronRight size={13} /></>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// ─── Canvas PESA Field ────────────────────────────────────────────────────────

export const PESAField = ({ externalState = null, isFullView = false, onNodeClick }) => {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const { darkMode } = useTheme();
  const { engine: localEngine, theoryMode, getProcessMapPro, currentPhase } = usePesa();

  const [hoveredNode,   setHoveredNode]   = useState(null);
  const [hoveredFamily, setHoveredFamily] = useState(null);

  const activeState = useMemo(
    () => externalState || localEngine?.state || {},
    [externalState, localEngine?.state]
  );

  const { families = [], agents = [], interactions = [] } = activeState;
  const visualMode   = currentPhase?.visualMode || 'network-pulse';
  const ambiance     = currentPhase?.ambiance   || 'default';
  const tensionLevel = localEngine?.case?.steps?.[activeState.currentStepIndex]?.narrativeArc?.tensionLevel ?? 2;
  const palette      = AMBIANCE_PALETTE[ambiance] || AMBIANCE_PALETTE.default;
  const tension      = TENSION_CONFIG[tensionLevel] || TENSION_CONFIG[2];
  const isFrozen     = visualMode === 'network-freeze';
  const isChaotic    = visualMode === 'network-chaos';

  const liveFamilies = useMemo(() => families.filter(f => f.isActive), [families]);

  const neurons = useMemo(() => {
    const count = liveFamilies.length || 1;
    return liveFamilies.map((f, i) => {
      const angle  = (i / count) * Math.PI * 2 - Math.PI / 2;
      const radius = isFullView ? 0.38 : 0.33;
      const familyAgents = agents.filter(a => a.family === f.name);
      const avgBienEtre  = familyAgents.length
        ? familyAgents.reduce((s, a) => s + (
            a.resources instanceof Map ? a.resources.get('bienEtre') : (a.resources?.bienEtre ?? 50)
          ), 0) / familyAgents.length
        : 50;
      return {
        id: f.name, name: f.name,
        narrativeRole: f.narrativeRole || null,
        isToxic: f.isToxic || false,
        agents: familyAgents,
        baseX: Math.cos(angle) * radius,
        baseY: Math.sin(angle) * radius,
        color: FAMILY_COLORS[f.name] || '#94a3b8',
        vitality: avgBienEtre / 100,
      };
    });
  }, [liveFamilies, agents, isFullView]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId, time = 0;

    const drawCrosshair = (x, y, size, color, alpha) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth   = 0.5;
      ctx.beginPath(); ctx.moveTo(x - size, y); ctx.lineTo(x + size, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y - size); ctx.lineTo(x, y + size); ctx.stroke();
      ctx.restore();
    };

    const drawTechLabel = (x, y, text, color, size = 9, align = 'center') => {
      ctx.save();
      ctx.font = `${size}px 'DM Mono', monospace`;
      ctx.fillStyle = color;
      ctx.textAlign = align;
      ctx.shadowBlur = 6; ctx.shadowColor = 'rgba(0,0,0,0.95)';
      ctx.fillText(text, x, y);
      ctx.shadowBlur = 0;
      ctx.restore();
    };

    const render = () => {
      if (!isFrozen) time += isChaotic ? 0.018 : 0.007;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width / dpr, h = canvas.height / dpr;
      const cx = w / 2, cy = h / 2;

      ctx.clearRect(0, 0, w, h);

      // Fond radial
      const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.75);
      bgGrad.addColorStop(0, palette.bg);
      bgGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, w, h);

      // Grille interne (plus fine que LabGrid pour ne pas doubler)
      ctx.save();
      ctx.globalAlpha = 0.012;
      ctx.strokeStyle = T.cyan;
      ctx.lineWidth = 0.4;
      for (let x = 0; x < w; x += 52) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,h); ctx.stroke(); }
      for (let y = 0; y < h; y += 52) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(w,y); ctx.stroke(); }
      ctx.restore();

      drawCrosshair(cx, cy, Math.min(w,h)*0.07, palette.particle, 0.07);

      // Rings
      [0.27, 0.41, 0.54].forEach((r) => {
        const radius = Math.min(w, h) * r;
        ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI*2);
        ctx.strokeStyle = 'rgba(14,165,233,0.04)';
        ctx.lineWidth = 0.5; ctx.setLineDash([3,9]); ctx.stroke(); ctx.setLineDash([]);
      });

      // Nodes
      const nodesWithPos = neurons.map(n => {
        const jx = isChaotic ? Math.sin(time*0.7+n.baseX*10)*tension.chaos*w : 0;
        const jy = isChaotic ? Math.cos(time*0.9+n.baseY*10)*tension.chaos*h : 0;
        return { ...n, x: cx+n.baseX*w+jx, y: cy+n.baseY*h+jy };
      });

      // Interactions
      interactions.forEach(link => {
        const from = nodesWithPos.find(n => n.id === link.source);
        const to   = nodesWithPos.find(n => n.id === link.target);
        if (!from || !to) return;
        ctx.beginPath(); ctx.moveTo(from.x, from.y);
        ctx.quadraticCurveTo(cx, cy, to.x, to.y);
        ctx.strokeStyle = `rgba(14,165,233,${tension.linkOpacity})`;
        ctx.lineWidth = (link.strength||0.5)*2; ctx.stroke();
      });

      // Theory mode edges
      if (theoryMode) {
        const proMap = getProcessMapPro?.() || { edges: [] };
        proMap.edges.forEach(edge => {
          const from = nodesWithPos.find(n => n.id === edge.from);
          const to   = nodesWithPos.find(n => n.id === edge.to);
          if (!from || !to) return;
          ctx.beginPath(); ctx.moveTo(from.x, from.y);
          ctx.quadraticCurveTo(cx, cy, to.x, to.y);
          ctx.strokeStyle = edge.weight < 0 ? `rgba(239,68,68,${tension.linkOpacity*1.5})` : `rgba(16,185,129,${tension.linkOpacity*1.5})`;
          ctx.lineWidth = 1 + Math.abs(edge.weight||0)*1.5; ctx.stroke();
        });
      }

      nodesWithPos.forEach(n => {
        const isHovered = n.id === hoveredNode;
        const pulse = isFrozen ? 0 : Math.sin(time*(isChaotic?4:2)+n.baseX*5)*1.5;
        const size  = (isFullView?20:18) + (1-n.vitality)*6 + pulse;

        // Aura
        const auraGrad = ctx.createRadialGradient(n.x,n.y,size*0.4, n.x,n.y,size*2.2);
        auraGrad.addColorStop(0, `${n.color}`); auraGrad.addColorStop(1,'transparent');
        ctx.fillStyle = auraGrad; ctx.beginPath(); ctx.arc(n.x,n.y,size*2.2,0,Math.PI*2); ctx.fill();

        // Body
        const nodeGrad = ctx.createRadialGradient(n.x-size*0.3,n.y-size*0.3,0, n.x,n.y,size);
        nodeGrad.addColorStop(0,`${n.color}`); nodeGrad.addColorStop(0.6,`${n.color}`); nodeGrad.addColorStop(1,`${n.color}`);
        ctx.beginPath(); ctx.arc(n.x,n.y,size,0,Math.PI*2);
        ctx.fillStyle = nodeGrad;
        ctx.shadowBlur = isHovered ? 28 : 12; ctx.shadowColor = n.color;
        ctx.fill(); ctx.shadowBlur = 0;
        ctx.strokeStyle = isHovered ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)';
        ctx.lineWidth = isHovered ? 1.5 : 0.75; ctx.stroke();

        if (isHovered) drawCrosshair(n.x,n.y,size*2.5,n.color,0.35);

        drawTechLabel(n.x, n.y-size-12, n.name.toUpperCase(), 'rgba(240,244,248,0.85)', 9);
        if (n.isToxic) drawTechLabel(n.x, n.y-size-22, '⚠ DOMINANT', 'rgba(239,68,68,0.65)', 8);

        // Vitality bar
        const bW=size*2.2, bH=2, bX=n.x-bW/2, bY=n.y+size+7;
        ctx.fillStyle='rgba(255,255,255,0.05)'; ctx.fillRect(bX,bY,bW,bH);
        const vC = n.vitality>0.6?T.green:n.vitality>0.35?T.accentG:T.red;
        ctx.fillStyle=vC; ctx.fillRect(bX,bY,bW*n.vitality,bH);

        // Orbits
        n.agents.forEach((agent, idx) => {
          const arch = agent.archetype||'neutre';
          const orb  = ARCHETYPE_ORBIT[arch]||{speed:0.5,radius:1.0,pulse:false,color:'#94a3b8'};
          const oR   = (size+12)*orb.radius;
          const ang  = time*orb.speed+(idx/(n.agents.length||1))*Math.PI*2;
          const ox   = n.x+Math.cos(ang)*oR, oy = n.y+Math.sin(ang)*oR;
          const dot  = orb.pulse ? 2+Math.sin(time*4+idx)*0.8 : 2;
          ctx.beginPath(); ctx.arc(ox,oy,dot,0,Math.PI*2);
          ctx.fillStyle=orb.color; ctx.shadowBlur=orb.pulse?6:3; ctx.shadowColor=orb.color;
          ctx.fill(); ctx.shadowBlur=0;
        });
      });

      // HUD metrics (style landing : DM Mono dense)
      const hud = [
        {label:'NODES',value:liveFamilies.length},
        {label:'AGENTS',value:agents.length},
        {label:'LIENS',value:interactions.length},
        {label:'TICK',value:localEngine?.currentTick||0},
      ];
      hud.forEach((item,i)=>{
        const x=16+i*72;
        ctx.save();
        ctx.globalAlpha=0.5;
        ctx.fillStyle='rgba(5,10,14,0.6)';
        ctx.fillRect(x-4,h-26,68,20);
        ctx.restore();
        drawTechLabel(x+28,h-18,item.label,'rgba(255,255,255,0.22)',7,'center');
        drawTechLabel(x+28,h-6,String(item.value),palette.particle,9,'center');
      });

      animId = requestAnimationFrame(render);
    };

    const handleResize = () => {
      const dpr=window.devicePixelRatio||1, el=containerRef.current;
      if(!el) return;
      canvas.width=el.clientWidth*dpr; canvas.height=el.clientHeight*dpr;
      ctx.scale(dpr,dpr);
    };

    handleResize(); window.addEventListener('resize',handleResize); render();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize',handleResize); };
  }, [neurons, interactions, theoryMode, darkMode, hoveredNode, isFullView, palette, tension, isFrozen, isChaotic, localEngine?.currentTick, getProcessMapPro, liveFamilies.length, agents.length, visualMode, ambiance]);

  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX-rect.left, y = e.clientY-rect.top;
    const found = neurons.find(n => {
      const nx=rect.width/2+n.baseX*rect.width, ny=rect.height/2+n.baseY*rect.height;
      return Math.hypot(x-nx,y-ny)<36;
    });
    setHoveredNode(found?.id||null);
    setHoveredFamily(found?{name:found.name,narrativeRole:found.narrativeRole}:null);
  };

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden">
      <canvas ref={canvasRef} className="w-full h-full cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={()=>{setHoveredNode(null);setHoveredFamily(null);}}
        onClick={()=>hoveredNode&&onNodeClick?.(hoveredNode)}
      />
      <AnimatePresence>
        {hoveredFamily?.narrativeRole && (
          <motion.div
            key="tooltip"
            initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:6}}
            transition={{duration:0.15}}
            className="absolute bottom-14 left-1/2 -translate-x-1/2 pointer-events-none z-20 max-w-[260px]"
            style={{
              padding:'10px 14px', borderRadius:'8px',
              background: T.bgPanel,
              border:`1px solid ${T.border}`,
              backdropFilter:'blur(12px)',
            }}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-1 h-1 rounded-full" style={{background:FAMILY_COLORS[hoveredFamily.name]||T.cyan}}/>
              <span className="text-[9px] font-mono tracking-widest uppercase" style={{color:FAMILY_COLORS[hoveredFamily.name]||T.cyan}}>{hoveredFamily.name}</span>
            </div>
            <p className="text-[10px] leading-relaxed italic" style={{color:T.textMute}}>{hoveredFamily.narrativeRole}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Start Screen ─────────────────────────────────────────────────────────────

const StartScreen = ({ scenario, engine, audioRef, analyserRef, isAudioPlaying, setIsAudioPlaying, initAudioAnalysis }) => (
  <div
    className="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden"
    style={{ background: T.bg }}
    onMouseEnter={initAudioAnalysis}
    onClick={initAudioAnalysis}
  >
    {/* Canvas ambiante */}
    <div className="fixed inset-0 z-0 opacity-20"><PESAField /></div>

    {/* Grille landing */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`,
        backgroundSize: '52px 52px',
        opacity: 0.3,
      }}
    />
    {/* Scanlines */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`,
      }}
    />

    <motion.div
      initial={{opacity:0,y:32}} animate={{opacity:1,y:0}} transition={{duration:0.8,ease:[0.16,1,0.3,1]}}
      className="relative z-10 w-full max-w-4xl mx-4"
    >
      <div
        className="rounded-3xl overflow-hidden"
        style={{
          background: `${T.bgPanel}f5`,
          border: `1px solid ${T.border}`,
          backdropFilter: 'blur(24px)',
          boxShadow: `0 0 120px rgba(0,0,0,0.85), 0 0 0 1px ${T.cyan}14`,
        }}
      >
        {/* Accent line top */}
        <div className="h-px" style={{ background: `linear-gradient(90deg, transparent, ${T.cyan}50, transparent)` }} />

        {/* Header */}
        <div className="px-10 pt-8 pb-6 border-b" style={{ borderColor: T.border }}>
          <div className="flex items-center gap-3 mb-5">
            <Pulse color={T.cyan} />
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase" style={{ color: `${T.cyan}80` }}>
              PESACORE — Simulation Active
            </span>
          </div>
          <h1 className="text-5xl font-light tracking-tight leading-tight mb-3" style={{ color: T.text }}>
            {scenario.title}
          </h1>
          <p className="text-base leading-relaxed max-w-xl italic" style={{ color: T.textMute }}>
            "{scenario.pedagogicalGoal}"
          </p>
        </div>

        <div className="grid grid-cols-2 gap-0">
          {/* Visualiseur */}
          <div className="px-10 py-8 border-r" style={{ borderColor: T.border }}>
            <p className="text-[10px] font-mono uppercase tracking-widest mb-4" style={{ color: T.textMute }}>Analyse spectrale</p>
            <div
              className="h-16 rounded-xl overflow-hidden mb-3"
              style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}
            >
              <SignalVisualizer analyserRef={analyserRef} isActive={isAudioPlaying} />
            </div>
            <audio ref={audioRef} src="/audio/intro-pesa.mp3"
              onPlay={()=>setIsAudioPlaying(true)} onPause={()=>setIsAudioPlaying(false)}
              onEnded={()=>setIsAudioPlaying(false)} autoPlay
            />
            <div className="flex items-center gap-2 mt-3">
              <div className={`w-1.5 h-1.5 rounded-full transition-all ${isAudioPlaying ? 'animate-pulse' : ''}`}
                style={{ background: isAudioPlaying ? T.green : 'rgba(255,255,255,0.1)' }} />
              <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>
                {isAudioPlaying ? 'Signal actif' : 'En attente'}
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="px-10 py-8 flex flex-col justify-center gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full" style={{ background: T.accentG }} />
                <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>Protocole</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: `${T.textDim}90` }}>
                La simulation démarre dans l'état présent du système. Vos décisions modifient les dynamiques en temps réel.
              </p>
            </div>

            <motion.button
              whileHover={{scale:1.02}} whileTap={{scale:0.98}}
              onClick={()=>{
                initAudioAnalysis();
                if (engine.state.currentPhase?.duration>0) engine._schedulePhaseAdvance(engine.state.currentPhase.duration);
                engine.state.isPhaseRunning=true;
                engine._notifyStore();
              }}
              className="relative group w-full py-5 rounded-2xl font-semibold text-base overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${T.cyan}cc, #0284c7bb)`,
                border: `1px solid ${T.cyan}50`,
                color: 'white',
                boxShadow: `0 0 40px ${T.cyan}25`,
              }}
            >
              <span className="relative flex items-center justify-center gap-3">
                <Zap size={18} className="group-hover:rotate-12 transition-transform duration-300"/>
                Entrer dans le champ vivant
              </span>
            </motion.button>

            <p className="text-[9px] font-mono text-center uppercase tracking-widest" style={{ color: T.textMute }}>
              Simulation systémique irréversible
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  </div>
);

// ─── Contextual Revelation ────────────────────────────────────────────────────

const ContextualRevelation = ({ revelations, onDismiss }) => (
  <AnimatePresence>
    {revelations.map((rev, i) => (
      <motion.div
        key={rev.id}
        initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:20,scale:0.95}}
        transition={{duration:0.3,delay:i*0.08}}
        className="absolute pointer-events-auto"
        style={{
          top: `${20+i*110}px`,
          right: '16px',
          width: '280px',
          background: T.bgPanel,
          border: `1px solid ${rev.color||T.border}`,
          borderRadius: '12px',
          backdropFilter: 'blur(16px)',
          padding: '14px 16px',
          boxShadow: `0 0 24px ${rev.glow||'rgba(14,165,233,0.12)'}`,
        }}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{background:rev.color||T.cyan}}/>
            <span className="text-[9px] font-mono uppercase tracking-widest" style={{color:rev.color||T.cyan}}>{rev.label}</span>
          </div>
          <button onClick={()=>onDismiss(rev.id)} style={{ color: T.textMute }} className="hover:text-white transition-colors">
            <X size={11}/>
          </button>
        </div>
        <p className="text-[11px] leading-relaxed" style={{ color: T.textMute }}>{rev.text}</p>
        {rev.cause && rev.effect && (
          <div className="flex items-center gap-1.5 mt-2.5 text-[10px] font-mono">
            <span className="truncate" style={{ color: T.textMute }}>{rev.cause}</span>
            <ArrowRight size={8} style={{ color: T.textMute }} className="shrink-0"/>
            <span className="font-medium truncate" style={{color:rev.color||T.cyan}}>{rev.effect}</span>
          </div>
        )}
      </motion.div>
    ))}
  </AnimatePresence>
);

// ─── Bottom Narrative Bar ─────────────────────────────────────────────────────

const NarrativeBar = ({ currentStep, currentPhase, tensionLevel, progress, scenario, engine }) => {
  const tensionColors = ['', '#64748b', T.cyan, T.accentG, '#f97316', T.red];
  const tensionLabels = ['', 'STABLE', 'ACTIF', 'TENDU', 'CRITIQUE', 'RUPTURE'];
  const tColor = tensionColors[tensionLevel] || T.cyan;

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none"
      style={{
        background: `linear-gradient(to top, ${T.bg}f5 0%, ${T.bg}80 60%, transparent 100%)`,
        padding: '24px 32px 20px',
      }}
    >
      <div className="flex items-end justify-between gap-6">
        <div className="max-w-sm">
          
          <h3 className="text-lg font-semibold leading-tight" style={{ color: T.textDim }}>
            {currentStep?.title||'Analyse en cours'}
          </h3>
          {currentStep?.problematisation && (
            <p className="text-[11px] italic mt-1 leading-snug" style={{ color: T.textMute }}>
              "{currentStep.problematisation}"
            </p>
          )}
        </div>

        <div className="flex-1 max-w-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>Progression</span>
            <span className="text-[9px] font-mono" style={{ color: T.textMute }}>{progress}%</span>
          </div>
          <div className="relative h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full"
              animate={{width:`${progress}%`}}
              transition={{duration:0.8,ease:'easeOut'}}
              style={{ background: `linear-gradient(90deg, ${T.cyan}, ${T.green})` }}
            />
          </div>
          <div className="flex justify-between mt-2">
            {scenario?.steps?.map((_,i)=>{
              const stepProgress = engine?.state?.currentStepIndex??0;
              return (
                <div key={i} className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                  style={{background:i<stepProgress?T.cyan:i===stepProgress?T.cyanG:`rgba(255,255,255,0.1)`}}
                />
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-end gap-1">
            {[1,2,3,4,5].map(i=>(
              <div key={i} className="w-1.5 rounded-sm transition-all duration-300"
                style={{
                  height:`${8+i*3}px`,
                  background: i<=tensionLevel ? tColor : 'rgba(255,255,255,0.08)',
                  boxShadow: i<=tensionLevel ? `0 0 4px ${tColor}60` : 'none',
                }}
              />
            ))}
          </div>
          <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: `${tColor}80` }}>
            {tensionLabels[tensionLevel]}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Action Drawer ────────────────────────────────────────────────────────────

const ActionDrawer = ({ isOpen, actions, isConstraintReached, maxActions, actionsThisPhase, onAction, onAdvance, currentPhase, currentStep }) => {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (isOpen) setExpanded(true); }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{y:'100%',opacity:0}} animate={{y:0,opacity:1}} exit={{y:'100%',opacity:0}}
          transition={{type:'spring',stiffness:280,damping:28}}
          className="absolute bottom-0 left-0 right-0 z-30 pointer-events-auto"
          style={{
            background: `${T.bgPanel}fc`,
            borderTop: `1px solid ${T.border}`,
            backdropFilter: 'blur(24px)',
          }}
        >
          {/* Accent line top */}
          <div className="h-px" style={{ background: `linear-gradient(90deg, transparent, ${T.cyan}40, transparent)` }} />

          <div className="flex items-center justify-between px-8 py-4 border-b" style={{ borderColor: T.border }}>
            <div className="flex items-center gap-3">
              <Pulse color={T.accentG} size={6}/>
              <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: `${T.accentG}90` }}>
                Phase d'action
              </span>
              {maxActions && (
                <StatusTag color={isConstraintReached ? 'red' : 'amber'}>
                  {isConstraintReached ? 'Limite atteinte' : `${actionsThisPhase}/${maxActions} action`}
                </StatusTag>
              )}
            </div>
            <button onClick={()=>setExpanded(v=>!v)} style={{ color: T.textMute }} className="hover:text-white transition-colors">
              {expanded ? <ChevronDown size={14}/> : <ChevronUp size={14}/>}
            </button>
          </div>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{height:0}} animate={{height:'auto'}} exit={{height:0}}
                className="overflow-hidden"
              >
                <div className="px-8 py-5">
                  {currentStep?.problematisation && (
                    <p className="text-sm italic mb-5 leading-relaxed" style={{ color: T.textMute }}>
                      "{currentStep.problematisation}"
                    </p>
                  )}

                  {currentPhase?.hint && !isConstraintReached && (
                    <div
                      className="flex items-start gap-2.5 px-4 py-3 rounded-xl mb-4"
                      style={{ background: `${T.accentG}06`, border: `1px solid ${T.accentG}18` }}
                    >
                      <Lightbulb size={11} className="shrink-0 mt-0.5" style={{ color: `${T.accentG}80` }}/>
                      <p className="text-[11px] italic leading-relaxed" style={{ color: `${T.accentG}80` }}>
                        {currentPhase.hint}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-2.5 mb-4">
                    {actions.map((action,idx)=>(
                      <motion.button
                        key={action.id}
                        initial={{opacity:0,x:-12}} animate={{opacity:isConstraintReached?0.3:1,x:0}}
                        transition={{delay:idx*0.06}}
                        whileHover={!isConstraintReached?{x:4}:{}}
                        onClick={()=>!isConstraintReached&&onAction(action)}
                        disabled={isConstraintReached}
                        className="group flex items-center gap-5 px-6 py-4 rounded-xl transition-all duration-200 text-left"
                        style={{
                          background: 'rgba(255,255,255,0.025)',
                          border: `1px solid ${isConstraintReached ? T.border0 : T.border}`,
                          cursor: isConstraintReached ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={e=>{if(!isConstraintReached)e.currentTarget.style.borderColor=`${T.cyan}50`;}}
                        onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;}}
                      >
                        <span className="text-[10px] font-mono shrink-0" style={{ color: T.textMute }}>
                          {String(idx+1).padStart(2,'0')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium" style={{ color: T.text }}>{action.label}</p>
                          {action.competencySignal && (
                            <p className="text-[10px] font-mono mt-0.5" style={{ color: `${T.accentG}70` }}>
                              ↳ {action.competencySignal.name}
                            </p>
                          )}
                        </div>
                        <ArrowRight size={13} className="shrink-0 transition-colors" style={{ color: T.textMute }} />
                      </motion.button>
                    ))}
                    {actions.length===0 && (
                      <p className="text-center text-sm font-mono py-4" style={{ color: T.textMute }}>
                        Aucune action disponible.
                      </p>
                    )}
                  </div>

                  <button
                    onClick={onAdvance}
                    className="w-full py-3.5 rounded-xl text-sm font-semibold tracking-wide transition-all"
                    style={{
                      background: `linear-gradient(135deg, ${T.cyan}40, ${T.purple}30)`,
                      border: `1px solid ${T.cyan}35`,
                      color: T.cyanG,
                    }}
                  >
                    Passer à l'étape suivante →
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Right Metrics Panel ──────────────────────────────────────────────────────

const MetricsPanel = ({ isOpen, parameters, initialParameters, currentSolution, phase, currentCase }) => {
  const getStatusColor = (val) => val>65 ? T.green : val>40 ? T.accentG : T.red;
  const paramKeys = Object.keys(parameters||{}).filter(k=>typeof parameters[k]==='number'&&!isNaN(parameters[k]));
  const formatLabel = k => k.replace(/([A-Z])/g,' $1').replace(/_/g,' ').replace(/^./,s=>s.toUpperCase()).trim();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{x:'100%',opacity:0}} animate={{x:0,opacity:1}} exit={{x:'100%',opacity:0}}
          transition={{type:'spring',stiffness:300,damping:30}}
          className="absolute top-0 right-0 bottom-0 z-20 pointer-events-auto overflow-y-auto"
          style={{
            width: '280px',
            background: `${T.bgPanel}f8`,
            borderLeft: `1px solid ${T.border}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Accent line */}
          <div className="absolute top-0 bottom-0 left-0 w-px" style={{ background: `linear-gradient(180deg, transparent, ${T.cyan}40, ${T.cyan}18, transparent)` }} />

          <div className="p-5 space-y-5">
            <p className="text-[9px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>
              Métriques système
            </p>

            {currentSolution && phase==='finished' && (
              <motion.div initial={{opacity:0,y:6}} animate={{opacity:1,y:0}}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl"
                style={{ background: `${T.green}07`, border: `1px solid ${T.green}20` }}>
                <CheckCircle2 size={13} style={{ color: T.green }} className="shrink-0"/>
                <div>
                  <p className="text-[10px] font-mono uppercase mb-0.5" style={{ color: `${T.green}70` }}>Résolu</p>
                  <p className="text-[11px] font-semibold" style={{ color: T.textDim }}>{currentSolution.name}</p>
                </div>
              </motion.div>
            )}

            <div className="space-y-2">
              {paramKeys.slice(0,8).map(k=>{
                const val = parameters[k]??0;
                const init = initialParameters?.[k];
                const delta = init!==undefined ? val-init : null;
                const color = getStatusColor(val);
                return (
                  <div key={k} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono" style={{ color: T.textMute }}>{formatLabel(k)}</span>
                      <div className="flex items-center gap-1.5">
                        {delta!==null && Math.abs(delta)>0 && (
                          <span className="text-[9px] font-mono" style={{ color: delta>0 ? T.green : T.red }}>
                            {delta>0?'↑':'↓'}{Math.abs(Math.round(delta))}
                          </span>
                        )}
                        <span className="text-[11px] font-mono font-bold" style={{ color: T.textDim }}>{val}%</span>
                      </div>
                    </div>
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <motion.div className="h-full rounded-full" animate={{width:`${val}%`}}
                        transition={{duration:0.7,ease:'easeOut'}} style={{background:color,opacity:0.85}}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ProbabilityBridge dans le panel metrics */}
            <div className="pt-4 border-t" style={{ borderColor: T.border }}>
              <ProbabilityBridge />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Left Intelligence Panel ──────────────────────────────────────────────────

const IntelPanel = ({ isOpen, engine, completedActions, currentStep, scenario }) => {
  const [tab, setTab] = useState('causal');

  const completedArr  = completedActions instanceof Set ? [...completedActions] : [];
  const lastActionId  = completedArr[completedArr.length-1]||null;
  const lastAction    = useMemo(()=>{
    if(!lastActionId) return null;
    return engine?.case?.steps?.flatMap(s=>s.actions||[]).find(a=>a.id===lastActionId)||null;
  },[lastActionId,engine?.case?.steps]);

  const globalCausal  = engine?.case?.simulationEngine?.causality||[];
  const stepCausal    = currentStep?.causality||[];
  const causalities   = [...stepCausal,...globalCausal].slice(0,6);
  const df            = lastAction?.deepFeedback;
  const hasFeedback   = df&&(df.whatHappened||df.whyItMatters||df.whatItCosts);

  const tabs = [
    {id:'causal',  label:'Causal',  icon:GitBranch},
    {id:'feedback',label:'Analyse', icon:Activity, disabled:!hasFeedback},
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{x:'-100%',opacity:0}} animate={{x:0,opacity:1}} exit={{x:'-100%',opacity:0}}
          transition={{type:'spring',stiffness:300,damping:30}}
          className="absolute top-0 left-0 bottom-0 z-20 pointer-events-auto overflow-y-auto"
          style={{
            width: '280px',
            background: `${T.bgPanel}f8`,
            borderRight: `1px solid ${T.border}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Accent line */}
          <div className="absolute top-0 bottom-0 right-0 w-px" style={{ background: `linear-gradient(180deg, transparent, ${T.accent}40, ${T.accent}18, transparent)` }} />

          <div className="p-5 space-y-4">
            <p className="text-[9px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>
              Intelligence causale
            </p>

            <div className="scrollbar-hide flex gap-1">
              {tabs.map(t=>(
                <button key={t.id} onClick={()=>!t.disabled&&setTab(t.id)} disabled={t.disabled}
                  className={`flex-1 py-1.5 rounded-lg text-[9px] font-mono uppercase tracking-wide transition-all border flex items-center justify-center gap-1 ${
                    t.disabled ? 'opacity-20 cursor-not-allowed' :
                    tab===t.id  ? 'bg-cyan-500/10 text-cyan-400' : 'text-white/30 hover:text-white/60'
                  }`}
                  style={{
                    borderColor: t.disabled ? T.border0 : tab===t.id ? `${T.cyan}30` : T.border,
                  }}>
                  <t.icon size={9}/>{t.label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {tab==='causal' && (
                <motion.div key="causal" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="space-y-2">
                  {causalities.length>0 ? causalities.map((c,i)=>(
                    <div key={i} className="px-3 py-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
                      <div className="flex items-center gap-2 text-[10px] mb-1.5">
                        <span className="truncate flex-1" style={{ color: T.textMute }}>{c.cause}</span>
                        <ArrowRight size={7} style={{ color: T.textMute }} className="shrink-0"/>
                        <span className={`font-medium flex-1 text-right truncate ${(c.weight||0)>0?'text-emerald-400':'text-red-400'}`}>{c.effect}</span>
                      </div>
                      <div className="h-0.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div className={`h-full rounded-full ${(c.weight||0)>0?'bg-emerald-500':'bg-red-500'}`}
                          style={{width:`${Math.abs((c.weight||0)*100)}%`,opacity:0.7}}/>
                      </div>
                    </div>
                  )):(
                    <p className="text-center py-8 text-[10px] font-mono italic" style={{ color: T.textMute }}>
                      Aucune causalité active
                    </p>
                  )}
                </motion.div>
              )}
              {tab==='feedback' && hasFeedback && (
                <motion.div key="feedback" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="space-y-2">
                  {[
                    {key:'whatHappened',label:"Ce qui s'est passé",  color: T.textDim},
                    {key:'whyItMatters',label:'Pourquoi ça compte',  color: T.accentG},
                    {key:'whatItCosts', label:'Ce que ça coûte',     color: T.red},
                  ].filter(({key})=>df?.[key]).map(({key,label,color})=>(
                    <div key={key} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
                      <p className="text-[9px] font-mono uppercase tracking-widest mb-1.5" style={{ color: T.textMute }}>{label}</p>
                      <p className="text-[11px] leading-relaxed" style={{ color }}>{df[key]}</p>
                    </div>
                  ))}
                  {df?.boostCondition && (
                    <div className="p-3 rounded-xl" style={{ background: `${T.green}05`, border: `1px solid ${T.green}18` }}>
                      <p className="text-[9px] font-mono uppercase tracking-widest mb-1.5" style={{ color: `${T.green}60` }}>Levier suivant</p>
                      <p className="text-[11px] italic leading-relaxed" style={{ color: `${T.green}80` }}>{df.boostCondition}</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Journal */}
            <div className="scrollbar-hide pt-4 border-t" style={{ borderColor: T.border }}>
              <p className="text-[9px] font-mono uppercase tracking-widest mb-3" style={{ color: T.textMute }}>Journal</p>
              <div className="scrollbar-hide space-y-1.5 max-h-48 overflow-y-auto">
                {(completedArr||[]).slice().reverse().slice(0,8).map((id,i)=>{
                  const act = engine?.case?.steps?.flatMap(s=>s.actions||[]).find(a=>a.id===id);
                  return (
                    <div key={i} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg"
                      style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <div className="w-0.5 h-full rounded-full self-stretch" style={{ background: `${T.accent}35` }}/>
                      <span className="text-[10px] truncate" style={{ color: T.textMute }}>{act?.label||id}</span>
                    </div>
                  );
                })}
                {!completedArr.length && (
                  <p className="text-[10px] font-mono italic" style={{ color: T.textMute }}>Aucune action encore.</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Floating Toolbar ─────────────────────────────────────────────────────────

const FloatingToolbar = ({
  leftOpen, rightOpen, intelOpen, metricsOpen,
  onToggleLeft, onToggleRight, onToggleIntel, onToggleMetrics,
  theoryMode, onToggleTheory,
  soundEnabled, onToggleSound,
  phase, onShowDebrief,
}) => {
  const tools = [
    { icon: leftOpen?PanelLeftClose:PanelLeftOpen, label: leftOpen?'Masquer':'Panneaux', onClick:onToggleLeft,   active:leftOpen  },
    { icon: Network,  label:'Intelligence causale', onClick:onToggleIntel,   active:intelOpen   },
    { icon: Activity, label:'Métriques',            onClick:onToggleMetrics, active:metricsOpen },
    { icon: School,   label:`Théorie ${theoryMode?'ON':'OFF'}`, onClick:onToggleTheory, active:theoryMode, color:'amber' },
    { icon: soundEnabled?Volume2:VolumeX, label:'Son', onClick:onToggleSound, active:soundEnabled },
  ];

  return (
    <div className="absolute top-5 left-1/2 -translate-x-1/2 z-30 pointer-events-auto">
      <div
        className="flex items-center gap-1 px-2 py-2 rounded-2xl"
        style={{
          background: `${T.bgPanel}ee`,
          border: `1px solid ${T.border}`,
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 32px rgba(0,0,0,0.6)',
        }}
      >
        {tools.map((t,i)=>(
          <button key={i} onClick={t.onClick} title={t.label}
            className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200`}
            style={{
              background: t.active
                ? t.color==='amber' ? `${T.accentG}18` : `${T.cyan}12`
                : 'transparent',
              color: t.active
                ? t.color==='amber' ? T.accentG : T.cyanG
                : T.textMute,
              border: t.active
                ? `1px solid ${t.color==='amber' ? `${T.accentG}30` : `${T.cyan}25`}`
                : '1px solid transparent',
            }}
          >
            <t.icon size={15}/>
          </button>
        ))}

        {phase==='finished' && (
          <>
            <div className="w-px h-5 mx-1" style={{ background: T.border }} />
            <button
              onClick={onShowDebrief}
              className="flex items-center gap-2 px-3 h-9 rounded-xl text-[10px] font-mono uppercase tracking-widest transition-all"
              style={{
                background: `${T.purple}18`,
                border: `1px solid ${T.purple}35`,
                color: T.purple,
              }}
            >
              <Sparkles size={12}/> Rapport
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Narrative Overlay ────────────────────────────────────────────────────────

const NarrativeOverlay = ({ currentPhase, currentStep, analyserRef, isAudioPlaying }) => {
  if (!currentPhase || currentPhase.type === 'action') return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
      <motion.div
        initial={{opacity:0,scale:0.97}} animate={{opacity:1,scale:1}}
        transition={{duration:0.5,ease:[0.16,1,0.3,1]}}
        className="max-w-xl w-full mx-8 pointer-events-auto"
      >
        <div
          className="text-center px-10 py-8 rounded-3xl"
          style={{
            background: `${T.bgPanel}ee`,
            border: `1px solid ${T.border}`,
            backdropFilter: 'blur(24px)',
            boxShadow: '0 0 80px rgba(0,0,0,0.7)',
          }}
        >
          {/* Accent top */}
          <div className="mb-6 flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, transparent, ${T.cyan}30)` }}/>
            <Pulse color={T.accentG} size={5}/>
            <div className="flex-1 h-px" style={{ background: `linear-gradient(to left, transparent, ${T.cyan}30)` }}/>
          </div>

          <p className="text-xl font-light leading-relaxed mb-5" style={{ color: T.textDim }}>
            {currentStep?.narrativeOverride || currentPhase?.narrative || currentStep?.description || 'Le système évolue…'}
          </p>

          <div className="w-full h-10 max-w-xs mx-auto mb-4 rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)' }}>
            <K2000VoiceScanner isActive analyserRef={analyserRef} darkMode/>
          </div>

          <div className="flex items-center justify-center gap-2">
            <div className="w-1 h-1 rounded-full animate-pulse" style={{ background: T.accentG }} />
            <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>Signal stable</span>
          </div>
        </div>

        {currentPhase?.theoreticalHook && (
          <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:0.3}}
            className="mt-3 px-5 py-3 rounded-2xl"
            style={{ background: `${T.cyan}06`, border: `1px solid ${T.cyan}18`, backdropFilter:'blur(12px)' }}>
            <p className="text-[9px] font-mono uppercase tracking-widest mb-1.5 flex items-center gap-1.5" style={{ color: `${T.cyan}60` }}>
              <BookOpen size={9}/>Ancrage théorique
            </p>
            <p className="text-[11px] italic leading-relaxed" style={{ color: `${T.cyanG}80` }}>{currentPhase.theoreticalHook}</p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

// ─── Loading ──────────────────────────────────────────────────────────────────

const LoadingScreen = () => (
  <div
    className="fixed inset-0 z-[1000] flex items-center justify-center"
    style={{ background: T.bg }}
  >
    {/* Grille loading */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`,
        backgroundSize: '52px 52px',
        opacity: 0.2,
      }}
    />
    <div className="relative flex flex-col items-center gap-8">
      <motion.div
        animate={{rotate:360}}
        transition={{duration:2,repeat:Infinity,ease:'linear'}}
        className="w-16 h-16 rounded-full border-2 border-t-transparent"
        style={{ borderColor: `${T.cyan}50`, borderTopColor: 'transparent' }}
      />
      <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>
        Synchronisation PESACORE
      </span>
    </div>
  </div>
);

// ─── Live Field Modal ─────────────────────────────────────────────────────────

export const LiveFieldModal = ({ isOpen, onClose }) => {
  const { engine } = usePesa();
  useEffect(()=>{
    if(!isOpen) return;
    const h=(e)=>{if(e.key==='Escape')onClose();};
    window.addEventListener('keydown',h); return ()=>window.removeEventListener('keydown',h);
  },[isOpen,onClose]);
  if(!engine?.state) return null;
  return createPortal(
    <AnimatePresence>
      {isOpen&&(
        <motion.div key="live-field" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background:'rgba(0,0,0,0.92)', backdropFilter:'blur(8px)' }}
          onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
          <motion.div initial={{scale:0.97,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.97,opacity:0}}
            className="relative overflow-hidden rounded-2xl"
            style={{
              width:'calc(100vw - 80px)', height:'calc(100vh - 80px)',
              background: T.bg,
              border: `1px solid ${T.border}`,
              boxShadow: `0 0 120px rgba(0,0,0,0.9), 0 0 0 1px ${T.cyan}14`,
            }}>
            <PESAField externalState={{...engine.state}} isFullView/>
            <button onClick={onClose}
              className="absolute top-5 right-5 z-10 flex items-center justify-center w-8 h-8 rounded-xl transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.border}` }}>
              <X size={14} style={{ color: T.textMute }}/>
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ─── PAGE PRINCIPALE ───────────────────────────────────────────────────────────

export default function CasePage() {
  const {
    engine, loadSimulation, restartSimulation,
    toggleTheoryMode, phase, scenario, currentStep,
    theoryMode, closeDebrief, showDebrief,
    currentSolution, closeSolution, toggleShowDebrief,
    applyAction, completedActions, unlockedActions,
    currentPhase, parameters, initialParameters,
  } = usePesa();

  const { caseId, slug }   = useParams();
  const navigate     = useNavigate();
  const [loading, setLoading] = useState(true);

  // ── [4] Debrief onboarding (même logique que GenericCase) ──────────────────
  const [showDebriefOnboarding, setShowDebriefOnboarding] = useState(false);
  const [showFinalReport,       setShowFinalReport]       = useState(false);
  const onboardingFiredRef = useRef(false);

  useEffect(() => {
    if (showDebrief && !onboardingFiredRef.current) {
      onboardingFiredRef.current = true;
      closeDebrief();                   // remet showDebrief=false dans le store
      setShowDebriefOnboarding(true);   // on ouvre l'onboarding à la place
    }
    if (!showDebrief && phase !== 'finished') {
      onboardingFiredRef.current = false;
    }
  }, [showDebrief, closeDebrief, phase]);

  // Audio
  const audioRef         = useRef(null);
  const audioContextRef  = useRef(null);
  const analyserRef      = useRef(null);
  const sourceRef        = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [soundEnabled,   setSoundEnabled]   = useState(true);

  // Panels
  const [intelOpen,   setIntelOpen]   = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [leftOpen,    setLeftOpen]    = useState(true);
  const [fieldModal,  setFieldModal]  = useState(false);

  // Revelations
  const [revelations, setRevelations] = useState([]);
  const revIdRef = useRef(0);

  const addRevelation = useCallback((rev) => {
    const id = ++revIdRef.current;
    setRevelations(r=>[...r.slice(-3),{...rev,id}]);
    setTimeout(()=>setRevelations(r=>r.filter(x=>x.id!==id)), 8000);
  },[]);

  const dismissRevelation = (id) => setRevelations(r=>r.filter(x=>x.id!==id));

  const initAudioAnalysis = async () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state==='suspended') await audioContextRef.current.resume();
      return;
    }
    const ctx = new (window.AudioContext||window.webkitAudioContext)();
    const analyser = ctx.createAnalyser(); analyser.fftSize=256;
    try {
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser); analyser.connect(ctx.destination);
      audioContextRef.current=ctx; analyserRef.current=analyser; sourceRef.current=source;
    } catch(e) { console.error('AudioSource error:',e); }
  };

  useEffect(() => {
    const fetchCase = async () => {
    try {
        let res;
        if (caseId || slug) {
        const id = caseId || slug;
        // Utiliser la route générique si l'identifiant est un slug (pas un ObjectId)
        const isSlug = /^[a-z0-9-]+$/.test(id) && !/^[0-9a-fA-F]{24}$/.test(id);
        res = await api.get(isSlug ? `/cases/${id}` : `/cases/generic/${id}`);

        loadSimulation(res.data);

        // ✅ Stockage avec le slug (toujours présent)
        const vizSlug = slug || caseId; // le slug est la référence commune
        if (res.data.visualization3D && typeof res.data.visualization3D === 'object') {
            localStorage.setItem(`pesa_viz_${vizSlug}`, JSON.stringify(res.data.visualization3D));
        }
        }
    } catch {
        toast.error("Échec de l'initialisation");
        navigate('/dashboard');
    } finally {
        setLoading(false);
    }
    };    

    if (engine?.case) {
        // L'affaire est déjà chargée, on arrête le loader
        setLoading(false);
        return;
    }

    if (caseId || slug) {
        fetchCase();
    }
    // Si ni caseId ni slug, on laisse loading à true (ou on gère autrement)
  }, [caseId, slug, engine?.case, loadSimulation, navigate]);

  // Sync localStorage
  useEffect(()=>{
    if(engine?.state&&caseId) {
      localStorage.setItem(`pesa_sync_${caseId}`,JSON.stringify({state:engine.state,case:engine.case,lastUpdate:Date.now()}));
    }
  },[engine?.state,engine?.case,caseId]);

  // Revelations auto depuis causalités fortes
  useEffect(()=>{
    if(!engine?.state) return;
    const causal = currentStep?.causality||[];
    const strong = causal.filter(c=>Math.abs(c.weight||0)>0.7).slice(0,1);
    strong.forEach(c=>{
      addRevelation({
        label:'Signal Causal',
        text:`${c.cause} → ${c.effect}`,
        cause:c.cause, effect:c.effect,
        color: c.weight>0 ? `${T.green}cc` : `${T.red}cc`,
        glow:  c.weight>0 ? `${T.green}18` : `${T.red}18`,
      });
    });
  },[currentStep]);

  // Open intel auto quand action complétée
  const completedArr = completedActions instanceof Set ? [...completedActions] : [];
  useEffect(()=>{
    if(completedArr.length>0) setIntelOpen(true);
  },[completedArr.length]);

  // Available actions
  const availableActions = useMemo(()=>{
    if(currentPhase?.type!=='action'||!currentPhase?.actionIds) return [];
    const safeUnlocked  = unlockedActions  instanceof Set ? unlockedActions  : new Set();
    const safeCompleted = completedActions instanceof Set ? completedActions : new Set();
    return currentPhase.actionIds.map(item=>{
      const id=typeof item==='string'?item:item.id;
      const action=currentStep?.actions?.find(a=>a.id===id);
      if(!action||safeCompleted.has(id)||!safeUnlocked.has(id)) return null;
      return action;
    }).filter(Boolean);
  },[currentPhase,currentStep,unlockedActions,completedActions]);

  const maxActions      = currentPhase?.constraint==='max-actions-1'?1:null;
  const safeCompleted   = completedActions instanceof Set ? completedActions : new Set();
  const actionsThisPhase= (currentPhase?.actionIds||[]).filter(id=>safeCompleted.has(typeof id==='string'?id:id.id)).length;
  const isConstraintReached = maxActions!==null&&actionsThisPhase>=maxActions;
  const isActionPhase   = currentPhase?.type==='action';

  const handleAction = (action) => {
    applyAction(action.id);
    const agents = engine?.state?.agents||[];
    const reacting = agents.find(a=>{
      if(a.reactionsTo instanceof Map) return a.reactionsTo.has(action.id);
      return !!a.reactionsTo?.[action.id];
    });
    if(reacting) {
      const reaction  = reacting.reactionsTo instanceof Map ? reacting.reactionsTo.get(action.id) : reacting.reactionsTo?.[action.id];
      const agentName = reacting.narrativeName||reacting.displayName||reacting.name;

      addRevelation({
        label: agentName,
        text:  reaction,
        color: `${T.accentG}cc`,
        glow:  `${T.accentG}14`,
      });

      toast.custom(()=>(
        <motion.div initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:40}}
          className="max-w-sm w-full rounded-xl p-4"
          style={{
            background: `${T.bgPanel}f8`,
            border: `1px solid ${T.accentG}25`,
            backdropFilter: 'blur(16px)',
          }}>
          <div className="flex items-start gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: `${T.accentG}12`, border: `1px solid ${T.accentG}25` }}
            >
              <span className="text-sm font-bold" style={{ color: T.accentG }}>{agentName.charAt(0)}</span>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: T.accentG }}>{agentName}</p>
              <p className="text-xs italic leading-relaxed" style={{ color: T.textMute }}>"{reaction}"</p>
            </div>
          </div>
        </motion.div>
      ),{duration:5500,position:'bottom-right'});
    }
  };

  const progress = useMemo(()=>{
    if(!scenario?.steps?.length) return 0;
    const total=scenario.steps.length, current=engine?.state?.currentStepIndex||0;
    return Math.min(100,Math.round((current/(total-1))*100));
  },[scenario,engine?.state?.currentStepIndex]);

  const tensionLevel = currentStep?.narrativeArc?.tensionLevel??2;

  if(loading||!scenario) return <LoadingScreen/>;

  if(window.innerWidth<1024) return (
    <div
      className="min-h-screen flex items-center justify-center p-6 text-center"
      style={{ background: T.bg, color: T.text }}
    >
      <div className="max-w-sm">
        <div className="text-5xl mb-6">💻</div>
        <h2 className="text-2xl font-bold mb-3">Simulation optimisée ordinateur</h2>
        <p className="text-sm leading-relaxed mb-6" style={{ color: T.textMute }}>
          Ouvrez sur un écran ≥ 1024px pour la meilleure expérience.
        </p>
        <button
          onClick={()=>window.history.back()}
          className="px-5 py-2.5 rounded-xl text-sm"
          style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.border}` }}
        >
          Retour
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen font-sans selection:bg-amber-500/30"
      style={{ background: T.bg, color: T.text }}
    >
      {/* Couches globales landing */}
      <LabGrid />
      <Scanlines />

      {/* [2] InsightOverlay — global */}
      <InsightOverlay />

      <Toaster position="bottom-right"/>

      {/* Start Screen */}
      {engine&&!engine.state.isPhaseRunning&&(
        <StartScreen
          scenario={scenario} engine={engine}
          audioRef={audioRef} analyserRef={analyserRef}
          isAudioPlaying={isAudioPlaying} setIsAudioPlaying={setIsAudioPlaying}
          initAudioAnalysis={initAudioAnalysis}
        />
      )}

      <div className="flex flex-col h-screen relative z-10">

        {/* HEADER — style landing top bar */}
        <header
          className="shrink-0 z-50 flex items-center justify-between px-6 py-0"
          style={{
            background: `${T.bgPanel}d8`,
            borderBottom: `1px solid ${T.border}`,
            backdropFilter: 'blur(16px)',
            height: '56px',
          }}
        >
          {/* Scanlines dans header */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`,
            }}
          />

          <div className="relative flex items-center gap-4">
            <button
              onClick={()=>navigate('/dashboard')}
              className="flex items-center justify-center w-8 h-8 rounded-xl transition-all"
              style={{ background:'rgba(255,255,255,0.04)', border:`1px solid ${T.border}` }}
            >
              <ChevronLeft size={16} style={{ color: T.textMute }}/>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: phase==='playing' ? T.green : T.accentG,
                    boxShadow: `0 0 6px ${phase==='playing' ? T.green : T.accentG}`,
                    animation: 'pulse 2s infinite',
                  }}
                />
                <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: T.textMute }}>{phase}</span>
              </div>
              <h1 className="text-sm font-semibold leading-tight truncate max-w-xs" style={{ color: T.textDim }}>
                {scenario.title}
              </h1>
            </div>
          </div>

          {/* Centre : status PESACORE landing style */}
          <div className="relative hidden md:flex items-center gap-4">
            <span className="text-[9px] font-mono tracking-[0.3em] uppercase" style={{ color: T.textMute }}>
              PESACORE · Simulation
            </span>
            <div style={{ width:1, height:14, background: T.border }} />
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: T.green, boxShadow: `0 0 6px ${T.green}` }} />
              <span className="text-[9px] font-mono tracking-widest" style={{ color: T.textMute }}>NOMINAL</span>
            </div>
          </div>

          <div className="relative flex items-center gap-2">
            <button
              onClick={restartSimulation}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-mono uppercase tracking-widest transition-all"
              style={{ background:`${T.red}0a`, border:`1px solid ${T.red}20`, color:`${T.red}90` }}
            >
              <History size={11}/> Reset
            </button>
            <button
              onClick={()=>setFieldModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-mono uppercase tracking-widest transition-all"
              style={{ background:`${T.cyan}0a`, border:`1px solid ${T.cyan}20`, color:`${T.cyan}90` }}
            >
              <Maximize2 size={11}/> Projection
            </button>
            <button
              onClick={() => window.open(`/viz3d/${caseId}`, '_blank')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-mono uppercase tracking-widest transition-all"
              style={{ background:`${T.green}0a`, border:`1px solid ${T.green}20`, color:`${T.cyan}90` }}
            >
              <View size={11}/> Vue 3D
            </button>
          </div>
        </header>

        {/* CANVAS ZONE */}
        <main className="flex-1 relative overflow-hidden">

          {/* Canvas PESA fond total */}
          <div className="absolute inset-0 z-0">
            <PESAField/>
          </div>

          {/* Floating Toolbar */}
          <FloatingToolbar
            leftOpen={leftOpen} rightOpen={metricsOpen}
            intelOpen={intelOpen} metricsOpen={metricsOpen}
            onToggleLeft={()=>setLeftOpen(v=>!v)}
            onToggleRight={()=>setMetricsOpen(v=>!v)}
            onToggleIntel={()=>setIntelOpen(v=>!v)}
            onToggleMetrics={()=>setMetricsOpen(v=>!v)}
            theoryMode={theoryMode} onToggleTheory={toggleTheoryMode}
            soundEnabled={soundEnabled} onToggleSound={()=>setSoundEnabled(v=>!v)}
            phase={phase} onShowDebrief={()=>setShowDebriefOnboarding(true)}
          />

          {/* Intel panel gauche */}
          <IntelPanel
            isOpen={intelOpen} engine={engine}
            completedActions={completedActions}
            currentStep={currentStep} scenario={scenario}
          />

          {/* Metrics panel droit */}
          <MetricsPanel
            isOpen={metricsOpen} parameters={parameters||{}}
            initialParameters={initialParameters||{}}
            currentSolution={currentSolution} phase={phase}
            currentCase={engine?.case}
          />

          {/* Overlay narratif */}
          <NarrativeOverlay
            currentPhase={currentPhase} currentStep={currentStep}
            analyserRef={analyserRef} isAudioPlaying={isAudioPlaying}
          />

          {/* Révélations contextuelles */}
          <div className="absolute inset-0 pointer-events-none z-20">
            <ContextualRevelation revelations={revelations} onDismiss={dismissRevelation}/>
          </div>

          {/* Action Drawer */}
          <ActionDrawer
            isOpen={isActionPhase}
            actions={availableActions}
            isConstraintReached={isConstraintReached}
            maxActions={maxActions}
            actionsThisPhase={actionsThisPhase}
            onAction={handleAction}
            onAdvance={()=>engine?.advancePhase()}
            currentPhase={currentPhase}
            currentStep={currentStep}
          />

          {/* Narrative Bar (hors action) */}
          {!isActionPhase && (
            <NarrativeBar
              currentStep={currentStep} currentPhase={currentPhase}
              tensionLevel={tensionLevel} progress={progress}
              scenario={scenario} engine={engine}
            />
          )}

        </main>
      </div>

      {/* ── Bouton rapport (phase finished, hors onboarding) ── */}
      {phase==='finished' && !showDebriefOnboarding && !showFinalReport && (
        <motion.button
          initial={{y:50,opacity:0}} animate={{y:0,opacity:1}}
          whileHover={{scale:1.05}} whileTap={{scale:0.95}}
          onClick={()=>setShowDebriefOnboarding(true)}
          className="fixed bottom-12 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-full font-semibold flex items-center gap-4 shadow-lg border"
          style={{
            background: `linear-gradient(135deg, ${T.purple}, ${T.gold})`,
            borderColor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            boxShadow: `0 0 40px ${T.purple}50, 0 0 80px ${T.gold}20`,
          }}
        >
          <Sparkles size={18} className="animate-pulse"/>
          CONSULTER LE RAPPORT
          <FileText size={18}/>
        </motion.button>
      )}

      {/* Live Field Modal */}
      <LiveFieldModal isOpen={fieldModal} onClose={()=>setFieldModal(false)}/>

      {/* ── [3] DebriefOnboarding ── */}
      <AnimatePresence>
        {showDebriefOnboarding && (
          <DebriefOnboarding
            onComplete={()=>{ setShowDebriefOnboarding(false); setShowFinalReport(true); }}
            onClose={()=>setShowDebriefOnboarding(false)}
          />
        )}
      </AnimatePresence>

      {/* ── RapportEnquete — uniquement via showFinalReport ── */}
      {showFinalReport && (
        <RapportEnquete
          onClose={()=>{ setShowFinalReport(false); closeDebrief(); }}
        />
      )}
    </div>
  );
}